import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  SvgIcon,
  Tooltip,
  styled,
} from "@mui/material";
import {
  CsConfigurationApi,
  useCsConfigurationApi,
} from "@plansoft-configuration-renderer/framework";

import CadIcon from "@/assets/svgs/CadIcon.svg?react";
import DwgIcon from "@/assets/svgs/DWG_grau.svg?react";
import SapIcon from "@/assets/svgs/SapIcon.svg?react";
import { PropertyValue, isButtonActive, isButtonVisible } from "@/tools/helper";

import LoadConfigurationDialog from "./LoadConfigurationDialog/LoadConfigurationDialog";
import SapDialog from "./SapDialog";

const StyledIconButton = styled(Button)(({ theme }) => ({
  width: "64px",
  padding: 0,
  "&.Mui-disabled": {
    color: theme.palette.action.disabled,
    pointerEvents: "none",
    "& svg path": {
      fill: theme.palette.action.disabled,
    },
  },
  "&:hover:not(.Mui-disabled)": {
    color: theme.palette.primary.main,
    "& svg path": {
      fill: theme.palette.primary.main,
    },
  },
}));

interface ConfigurationActionsProps {
  properties: Record<string, PropertyValue>;
  onCadClick: () => Promise<void>;
  onDrawingClick: (api: CsConfigurationApi) => Promise<void>;
  onSaveClick: (type: "FAV" | "SC" | "SAP") => Promise<string>;
  onAddToCartClick: () => void;
  onGenerateDocumentClick: () => Promise<void>;
  isGeneratingDocument: boolean;
  isGeneratingDrawing: boolean;
}

export default function ConfigurationActions({
  properties,
  onCadClick,
  onDrawingClick,
  onSaveClick,
  onAddToCartClick,
  onGenerateDocumentClick,
  isGeneratingDocument,
  isGeneratingDrawing,
}: ConfigurationActionsProps): ReactNode {
  const { t } = useTranslation();
  const api = useCsConfigurationApi();

  return (
    <Box sx={{ display: "flex" }}>
      {isButtonVisible(properties, "SAP") && (
        <SapDialog
          handleSaveClick={() => onSaveClick("SAP")}
          renderTrigger={({ onClick }) => (
            <Tooltip title={t("sap_button")} arrow>
              <StyledIconButton
                disabled={!isButtonActive(properties, "SAP")}
                onClick={onClick}
              >
                <SvgIcon component={SapIcon} inheritViewBox />
              </StyledIconButton>
            </Tooltip>
          )}
        />
      )}
      {isButtonVisible(properties, "CAD") && (
        <Tooltip title={t("cad_button")} arrow>
          <StyledIconButton
            onClick={onCadClick}
            disabled={!isButtonActive(properties, "CAD")}
          >
            <SvgIcon component={CadIcon} inheritViewBox />
          </StyledIconButton>
        </Tooltip>
      )}
      {isButtonVisible(properties, "DRAWING") && (
        <Tooltip title={t("drawing_button")} arrow>
          <StyledIconButton
            onClick={() => onDrawingClick(api)}
            disabled={
              !isButtonActive(properties, "DRAWING") || isGeneratingDrawing
            }
          >
            {isGeneratingDrawing ? (
              <CircularProgress size={28} />
            ) : (
              <SvgIcon component={DwgIcon} inheritViewBox />
            )}
          </StyledIconButton>
        </Tooltip>
      )}
      {isButtonVisible(properties, "DOC") && (
        <Tooltip title={t("generate_document")} arrow>
          <StyledIconButton
            onClick={onGenerateDocumentClick}
            disabled={
              !isButtonActive(properties, "DOC") || isGeneratingDocument
            }
          >
            {isGeneratingDocument ? (
              <CircularProgress size={28} />
            ) : (
              <PictureAsPdfOutlinedIcon sx={{ fontSize: "28px !important" }} />
            )}
          </StyledIconButton>
        </Tooltip>
      )}

      <Divider orientation="vertical" sx={{ mx: 1, height: "3rem" }} />

      {isButtonVisible(properties, "LOAD") && (
        <LoadConfigurationDialog
          renderTrigger={({ onClick }) => (
            <Tooltip title={t("load")} arrow>
              <StyledIconButton
                variant="text"
                onClick={onClick}
                disabled={!isButtonActive(properties, "LOAD")}
              >
                <SvgIcon component={SaveAltIcon} inheritViewBox />
              </StyledIconButton>
            </Tooltip>
          )}
        />
      )}
      {isButtonVisible(properties, "SAVE") && (
        <Tooltip title={t("save")} arrow>
          <StyledIconButton
            disabled={!isButtonActive(properties, "SAVE")}
            onClick={() => onSaveClick("FAV")}
          >
            <SvgIcon component={SaveOutlinedIcon} inheritViewBox />
          </StyledIconButton>
        </Tooltip>
      )}
      {isButtonVisible(properties, "CART") && (
        <Tooltip title={t("add_to_cart")} arrow>
          <StyledIconButton
            disabled={!isButtonActive(properties, "CART")}
            onClick={onAddToCartClick}
          >
            <SvgIcon component={ShoppingCartOutlinedIcon} inheritViewBox />
          </StyledIconButton>
        </Tooltip>
      )}
    </Box>
  );
}
